import { Component, useEffect, type ErrorInfo, type ReactNode } from "react"

import { Button } from "@coval/ui"
import { RefreshCw } from "lucide-react"

import { markErrorFallbackShown } from "@/lib/appErrorFallback"
import { logger } from "@/lib/logger"
import { MotionPauseButton } from "@/components/MotionPauseButton"
import { usePausableMotion } from "@/hooks/usePausableMotion"

import { ErrorParticleBurst } from "./ErrorParticleBurst"

const log = logger("error-boundary")

interface Props {
  children: ReactNode
  /** Identifies which boundary caught the error in telemetry. */
  level: "app" | "route"
  /**
   * When this value changes, a previously-caught error is cleared. The route
   * boundary passes the current path so navigating away recovers the UI without
   * a full reload.
   */
  resetKey?: string
}

interface State {
  hasError: boolean
}

/**
 * Catches render-time errors in the React tree below it and shows a fallback
 * instead of an unmounted (white-screen) app. Errors are reported through the
 * shared logger, which forwards to Datadog RUM. Does not catch errors in event
 * handlers, async code, or server-side rendering.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error(`Unhandled render error (${this.props.level} boundary)`, error, {
      boundary: this.props.level,
      componentStack: info.componentStack,
    })
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback level={this.props.level} />
    }
    return this.props.children
  }
}

function ErrorFallback({ level }: { level: Props["level"] }) {
  // App-shell surfaces mounted above this boundary — the Sofia panel — keep
  // rendering over the error screen unless told to stand down. Publishing from
  // the fallback itself ties the signal to what is actually on screen, so
  // recovering (navigating away, remounting) clears it with no second reset.
  useEffect(markErrorFallbackShown, [])
  const { isStill, toggleStill } = usePausableMotion()

  // The app boundary is the document-level fallback, so <main> is the correct
  // landmark. The route boundary renders inside a layout that already owns the
  // page's <main>, so use a neutral container to avoid two <main> landmarks.
  const Root: "main" | "div" = level === "app" ? "main" : "div"
  return (
    <Root className="relative flex min-h-screen items-center justify-center overflow-hidden bg-surface-contrast px-6 py-12 text-coval-text">
      <ErrorParticleBurst isStill={isStill} />
      <section className="relative flex flex-col items-center gap-6 text-center">
        <h1 className="text-lg font-semibold tracking-normal text-coval-text sm:text-xl">
          Something went wrong
        </h1>
        <Button type="button" onClick={() => window.location.reload()}>
          <RefreshCw aria-hidden="true" />
          Reload
        </Button>
      </section>
      <MotionPauseButton
        isStill={isStill}
        onToggle={toggleStill}
        className="absolute right-4 bottom-4"
      />
    </Root>
  )
}
